import axios from 'axios';

const BASE = '/api';
const STORAGE_KEY = 'mdvr_auth';

// ── Auth state ────────────────────────────────────────────────
let auth = null;

export const setAuth = (data) => {
  auth = data;
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
};

export const getAuth = () => {
  if (auth) return auth;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      auth = JSON.parse(raw);
      console.log('[Auth] Loaded from storage:', !!auth);
      return auth;
    }
  } catch {}
  console.log('[Auth] Loaded from storage:', false);
  return null;
};

export const clearAuth = () => {
  auth = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
};

// ── Session expiry interceptor ────────────────────────────────
// CMSV6 returns result:-3 for expired/invalid sessions (HTTP 200 body).
// HTTP 401 from the proxy also signals the same condition.
axios.interceptors.response.use(
  (response) => {
    if (response.data?.result === -3) {
      clearAuth();
      window.dispatchEvent(new Event('mdvr:session-expired'));
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      clearAuth();
      window.dispatchEvent(new Event('mdvr:session-expired'));
    }
    return Promise.reject(error);
  }
);

// ── Query helper ──────────────────────────────────────────────
const q = (params) => {
  const p = { ...params };
  if (auth) { p.jsession = auth.jsession; p.account = auth.account; }
  return p;
};

// ── Auth ──────────────────────────────────────────────────────
export const login = (account, password) =>
  axios.post(`${BASE}/login`, { account, password }).then(r => r.data);

export const logout = () =>
  axios.post(`${BASE}/logout`, { jsession: auth?.jsession, account: auth?.account }).then(r => r.data);

// ── Vehicles ──────────────────────────────────────────────────
export const getVehicles = () =>
  axios.get(`${BASE}/vehicles`, { params: q({}) }).then(r => r.data);

export const getVehicleStatus = (vehiIdno = '') =>
  axios.get(`${BASE}/vehicles/status`, { params: q({ vehiIdno }) }).then(r => r.data);

export const getPositions = () =>
  axios.get(`${BASE}/vehicles/positions`, { params: q({}) }).then(r => r.data);

// ── GPS Track ─────────────────────────────────────────────────
export const getTrack = (vehiIdno, startTime, endTime, pageIndex = 1) =>
  axios.get(`${BASE}/track`, { params: q({ vehiIdno, startTime, endTime, pageIndex }) }).then(r => r.data);

// ── Alarms ────────────────────────────────────────────────────
export const getAlarms = (vehiIdno = '', startTime, endTime, alarmType = '', pageIndex = 1) =>
  axios.get(`${BASE}/alarms`, { params: q({ vehiIdno, startTime, endTime, alarmType, pageIndex }) }).then(r => r.data);

// ── Video ─────────────────────────────────────────────────────
export const getVideoFiles = (vehiIdno, startTime, endTime, channelNo = 1, pageIndex = 1) =>
  axios.get(`${BASE}/videos`, { params: q({ vehiIdno, startTime, endTime, channelNo, pageIndex }) }).then(r => r.data);

// Day-based file list from port 6607 — returns files[]{beg,end,chn,PlaybackUrlWs,...}
export const getVideoFilesDay = (devIdno, year, mon, day, stream = 1) =>
  axios.get(`${BASE}/videofiles`, { params: q({ devIdno, year, mon, day, stream }) }).then(r => r.data);

export const getPlaybackUrl = (vehiIdno, startTime, endTime, channelNo = 1) =>
  axios.get(`${BASE}/video/playback`, { params: q({ vehiIdno, startTime, endTime, channelNo }) }).then(r => r.data);

export const getLiveUrl = (vehiIdno, channelNo = 1) =>
  axios.get(`${BASE}/video/live`, { params: q({ vehiIdno, channelNo }) }).then(r => r.data);

// ── Users ─────────────────────────────────────────────────────
export const getUsers = (pageIndex = 1) =>
  axios.get(`${BASE}/users`, { params: q({ pageIndex }) }).then(r => r.data);

export const addUser = (payload) =>
  axios.post(`${BASE}/users/add`, { ...q({}), ...payload }).then(r => r.data);

export const updateUser = (payload) =>
  axios.post(`${BASE}/users/update`, { ...q({}), ...payload }).then(r => r.data);

export const deleteUser = (targetAccount) =>
  axios.post(`${BASE}/users/delete`, { ...q({}), targetAccount }).then(r => r.data);

// ── Drivers ───────────────────────────────────────────────────
export const getDrivers = () =>
  axios.get(`${BASE}/drivers`, { params: q({}) }).then(r => r.data);

// ── Reports ───────────────────────────────────────────────────
export const getTripReport = (vehiIdno = '', startTime, endTime) =>
  axios.get(`${BASE}/reports/trip`, { params: q({ vehiIdno, startTime, endTime }) }).then(r => r.data);

// ── Geofence ──────────────────────────────────────────────────
export const getGeofences = () =>
  axios.get(`${BASE}/geofence`, { params: q({}) }).then(r => r.data);
